import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors} from '@angular/forms';
import {Subscription} from 'rxjs';
import {IpopConfiguration, LayoutPositionOption, SiteWideWidgetType} from '../../../../../model/ipop-configuration.model';
import {SiteWideUnlockForm} from './site-wide-unlock-settings';

@Component({
  selector: 'auto-site-wide-unlock',
  templateUrl: './site-wide-unlock.component.html',
  styleUrls: ['./site-wide-unlock.component.scss'],
  standalone: false
})
export class SiteWideUnlockComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: IpopConfiguration | null = null;
  @Input() accountId: number;
  @Input() isSaving = false;
  @Output() formGroupInitialized = new EventEmitter<SiteWideUnlockForm>();

  readonly form: SiteWideUnlockForm = new FormGroup({
    siteWideUnlockEnabled: new FormControl(false, {nonNullable: true}),
    bannerEnabled: new FormControl(false, {nonNullable: true}),
    unlockPopupEnabled: new FormControl(false, {nonNullable: true}),
    navigationPopupEnabled: new FormControl(false, {nonNullable: true}),
    unlockDesktop: new FormControl<number | null>(null),
    navigationDesktop: new FormControl<number | null>(null)
  });
  desktopOptions: LayoutPositionOption[] = [];
  settingsReady = false;
  private changes: Subscription;

  ngOnInit(): void
  {
    this.form.setValidators(() => this.validateSettings());
    this.changes = this.form.valueChanges.subscribe(() => this.updateDisabledState());
    this.updateDisabledState();
    this.formGroupInitialized.emit(this.form);
  }

  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.accountId) {
      this.settingsReady = false;
      this.desktopOptions = [];
      this.form.reset({}, {emitEvent: false});
    }
    if ((changes.data || changes.accountId) && this.data?.accountId === this.accountId) {
      this.loadSettings(this.data);
    }
    this.updateDisabledState();
  }

  ngOnDestroy(): void
  {
    this.changes?.unsubscribe();
  }

  private loadSettings(data: IpopConfiguration): void
  {
    this.desktopOptions = Array.isArray(data.layoutPositionOptions) ? data.layoutPositionOptions : [];
    this.settingsReady = Array.isArray(data.widgetSettings) && this.hasValidOptions();
    const banner = this.findWidget(data, 'site_wide_unlock_banner');
    const popup = this.findWidget(data, 'site_wide_unlock_popup');
    const navigation = this.findWidget(data, 'site_wide_unlock_navigation');
    this.form.reset({
      siteWideUnlockEnabled: data.siteWideUnlockEnabled ?? false,
      bannerEnabled: banner?.enabled ?? false,
      unlockPopupEnabled: popup?.enabled ?? false,
      navigationPopupEnabled: navigation?.enabled ?? false,
      unlockDesktop: popup?.positionLayoutId ?? null,
      navigationDesktop: navigation?.positionLayoutId ?? null
    }, {emitEvent: false});
  }

  private findWidget(data: IpopConfiguration, widgetType: SiteWideWidgetType)
  {
    const widget = data.widgetSettings?.find(row => row.widgetType === widgetType);

    return widget;
  }

  private hasValidOptions(): boolean
  {
    const valid = this.desktopOptions.length > 0
      && this.desktopOptions.every(option => option !== null && typeof option === 'object'
        && Number.isSafeInteger(option.id) && option.id > 0
        && typeof option.label === 'string' && option.label.trim().length > 0)
      && new Set(this.desktopOptions.map(option => option.id)).size === this.desktopOptions.length;

    return valid;
  }

  private validateSettings(): ValidationErrors | null
  {
    let errors: ValidationErrors | null = null;
    const values = this.form.getRawValue();
    const placementsValid = !values.siteWideUnlockEnabled || (
      this.hasValidSelection(values.unlockDesktop, values.unlockPopupEnabled)
      && this.hasValidSelection(values.navigationDesktop, values.navigationPopupEnabled)
    );
    if (!this.settingsReady || !placementsValid) {
      errors = {siteWidePlacement: true};
    }

    return errors;
  }

  private hasValidSelection(id: number | null, enabled: boolean): boolean
  {
    let valid = !enabled && id === null;
    if (id !== null) {
      valid = this.desktopOptions.some(option => option?.id === id);
    }

    return valid;
  }

  private updateDisabledState(): void
  {
    const ready = this.settingsReady && this.data?.accountId === this.accountId
      && this.data?.canConfigureSiteWideUnlock === true && !this.isSaving;
    const enabled = ready && this.form.controls.siteWideUnlockEnabled.value;
    this.setDisabled(this.form.controls.siteWideUnlockEnabled, !ready);
    for (const name of ['bannerEnabled', 'unlockPopupEnabled', 'navigationPopupEnabled'] as const) {
      this.setDisabled(this.form.controls[name], !enabled);
    }
    this.setDisabled(this.form.controls.unlockDesktop, !enabled || !this.form.controls.unlockPopupEnabled.value);
    this.setDisabled(this.form.controls.navigationDesktop, !enabled || !this.form.controls.navigationPopupEnabled.value);
    this.form.updateValueAndValidity({emitEvent: false});
  }

  private setDisabled(control: AbstractControl, disabled: boolean): void
  {
    if (disabled && control.enabled) {
      control.disable({emitEvent: false});
    }
    if (!disabled && control.disabled) {
      control.enable({emitEvent: false});
    }
  }
}
